import React, { useState, useEffect } from 'react';

function FieldMappingBuilder({ 
  fieldMappings = {}, 
  onMappingsChange, 
  sampleResponse = null,
  inventoryFields = []
}) {
  const [mappings, setMappings] = useState(fieldMappings);
  const [availablePaths, setAvailablePaths] = useState([]);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingField, setEditingField] = useState('');
  const [mappingType, setMappingType] = useState('simple');
  const [arrayPath, setArrayPath] = useState('');
  const [filterField, setFilterField] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [targetField, setTargetField] = useState('');
  const [simplePath, setSimplePath] = useState('');

  useEffect(() => {
    if (sampleResponse) {
      const paths = analyzeSampleResponse(sampleResponse);
      setAvailablePaths(paths);
    }
  }, [sampleResponse]);

  useEffect(() => {
    if (onMappingsChange) {
      onMappingsChange(mappings);
    }
  }, [mappings, onMappingsChange]);

  const analyzeSampleResponse = (data, prefix = '', maxDepth = 4, currentDepth = 0) => {
    const paths = [];
    
    if (currentDepth >= maxDepth) {
      return paths;
    }

    if (Array.isArray(data)) {
      if (prefix) {
        paths.push({
          path: prefix,
          type: 'array',
          description: `Array with ${data.length} items`,
          sample: data.length > 0 ? JSON.stringify(data[0]).substring(0, 100) + '...' : '[]'
        });
      }
      
      if (data.length > 0) {
        const firstElement = data[0];
        if (typeof firstElement === 'object' && firstElement !== null) {
          Object.keys(firstElement).forEach(key => {
            const currentPath = prefix ? `${prefix}[].${key}` : `${key}`;
            const value = firstElement[key];
            
            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
              const nestedPaths = analyzeSampleResponse(value, currentPath, maxDepth, currentDepth + 1);
              paths.push(...nestedPaths);
            } else {
              paths.push({
                path: currentPath,
                type: Array.isArray(value) ? 'array' : typeof value,
                description: `Field in array items`,
                sample: Array.isArray(value) ? '[...]' : String(value).substring(0, 50)
              });
            }
          });
        }
      }
    } else if (typeof data === 'object' && data !== null) {
      Object.keys(data).forEach(key => {
        const currentPath = prefix ? `${prefix}.${key}` : key;
        const value = data[key];
        
        if (Array.isArray(value)) {
          paths.push({
            path: currentPath,
            type: 'array',
            description: `Array with ${value.length} items`,
            sample: value.length > 0 ? JSON.stringify(value[0]).substring(0, 100) + '...' : '[]'
          });
          
          if (value.length > 0 && typeof value[0] === 'object') {
            Object.keys(value[0]).forEach(subKey => {
              const subValue = value[0][subKey];
              paths.push({
                path: `${currentPath}[].${subKey}`,
                type: typeof subValue,
                description: `Field in ${key} array items`,
                sample: String(subValue).substring(0, 50)
              });
            });
          }
        } else if (typeof value === 'object' && value !== null) {
          const nestedPaths = analyzeSampleResponse(value, currentPath, maxDepth, currentDepth + 1);
          paths.push(...nestedPaths);
        } else {
          paths.push({
            path: currentPath,
            type: typeof value,
            description: `Simple field`,
            sample: String(value).substring(0, 50)
          });
        }
      });
    }

    return paths;
  };

  const addMapping = () => {
    const newField = `Field_${Object.keys(mappings).length + 1}`;
    setEditingField(newField);
    setShowBuilder(true);
    resetBuilder();
  };

  const editMapping = (fieldName) => {
    setEditingField(fieldName);
    setShowBuilder(true);
    
    const currentMapping = mappings[fieldName] || '';
    
    // Parse existing mapping to populate form
    if (currentMapping.includes('[') && currentMapping.includes(']')) {
      if (currentMapping.includes('=')) {
        // Filtered mapping: "crew[job=Director].name"
        const match = currentMapping.match(/^(.+?)\[(.+?)=(.+?)\]\.(.+)$/);
        if (match) {
          setMappingType('filtered');
          setArrayPath(match[1]);
          setFilterField(match[2]);
          setFilterValue(match[3]);
          setTargetField(match[4]);
        }
      } else {
        // Get all mapping: "cast[].name"
        const match = currentMapping.match(/^(.+?)\[\]\.(.+)$/);
        if (match) {
          setMappingType('all');
          setArrayPath(match[1]);
          setTargetField(match[2]);
        }
      }
    } else {
      // Simple mapping
      setMappingType('simple');
      setSimplePath(currentMapping);
    }
  };

  const resetBuilder = () => {
    setMappingType('simple');
    setArrayPath('');
    setFilterField('');
    setFilterValue('');
    setTargetField('');
    setSimplePath('');
  };

  const saveMapping = () => {
    if (!editingField.trim()) return;
    
    let mappingExpression = '';
    
    switch (mappingType) {
      case 'simple':
        mappingExpression = simplePath;
        break;
      case 'filtered':
        if (arrayPath && filterField && filterValue && targetField) {
          mappingExpression = `${arrayPath}[${filterField}=${filterValue}].${targetField}`;
        }
        break;
      case 'all':
        if (arrayPath && targetField) {
          mappingExpression = `${arrayPath}[].${targetField}`;
        }
        break;
    }
    
    if (mappingExpression) {
      setMappings(prev => ({
        ...prev,
        [editingField]: mappingExpression
      }));
    }
    
    setShowBuilder(false);
    setEditingField('');
    resetBuilder();
  };

  const deleteMapping = (fieldName) => {
    setMappings(prev => {
      const newMappings = { ...prev };
      delete newMappings[fieldName];
      return newMappings;
    });
  };

  const getArrayPaths = () => {
    return availablePaths.filter(p => p.type === 'array').map(p => p.path);
  };

  const getFieldsForArray = (arrayPath) => {
    const prefix = `${arrayPath}[].`;
    return availablePaths
      .filter(p => p.path.startsWith(prefix))
      .map(p => p.path.substring(prefix.length));
  };

  const getFilterValuesForField = (arrayPath, filterField) => {
    if (!sampleResponse || !arrayPath || !filterField) return [];
    
    const arrayData = extractValueFromPath(sampleResponse, arrayPath);
    if (!Array.isArray(arrayData)) return [];
    
    const values = new Set();
    arrayData.forEach(item => {
      if (item && typeof item === 'object' && item[filterField] !== undefined) {
        values.add(item[filterField]);
      }
    });
    
    return Array.from(values).sort();
  };

  const extractValueFromPath = (data, path) => {
    if (!path) return data;
    
    const pathParts = path.split('.');
    let current = data;
    
    for (const part of pathParts) {
      if (current && typeof current === 'object' && current[part] !== undefined) {
        current = current[part];
      } else {
        return null;
      }
    }
    
    return current;
  };

  return (
    <div style={{ marginBottom: '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h4>Field Mappings</h4>
        <button type="button" className="btn btn-secondary" onClick={addMapping}>
          Add Mapping
        </button>
      </div>

      {/* Current Mappings */}
      <div style={{ marginBottom: '1rem' }}>
        {Object.keys(mappings).length === 0 ? (
          <p style={{ color: '#666', fontStyle: 'italic' }}>No field mappings defined yet.</p>
        ) : (
          <div style={{ border: '1px solid #ddd', borderRadius: '4px' }}>
            {Object.entries(mappings).map(([fieldName, expression]) => (
              <div key={fieldName} style={{ 
                padding: '0.75rem', 
                borderBottom: '1px solid #eee',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <strong>{fieldName}</strong>
                  <br />
                  <code style={{ fontSize: '0.9rem', color: '#666' }}>{expression}</code>
                </div>
                <div>
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => editMapping(fieldName)}
                    style={{ marginRight: '0.5rem', fontSize: '0.8rem' }}
                  >
                    Edit
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-danger"
                    onClick={() => deleteMapping(fieldName)}
                    style={{ fontSize: '0.8rem' }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Mapping Builder Modal */}
      {showBuilder && (
        <div className="modal" style={{ zIndex: 10001 }}>
          <div className="modal-content" style={{ maxWidth: '800px', width: '90vw' }}>
            <h3>{editingField ? `Edit Mapping: ${editingField}` : 'Add Field Mapping'}</h3>
            
            <div className="form-group">
              <label>Field Name (in your inventory type)</label>
              <select
                value={editingField}
                onChange={(e) => setEditingField(e.target.value)}
              >
                <option value="">-- Select a field --</option>
                {inventoryFields.map((field, index) => (
                  <option key={index} value={field.name}>
                    {field.name} ({field.type})
                  </option>
                ))}
              </select>
              {inventoryFields.length === 0 && (
                <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem' }}>
                  No fields available. Make sure an inventory type is selected with defined fields.
                </p>
              )}
            </div>

            <div className="form-group">
              <label>Mapping Type</label>
              <select value={mappingType} onChange={(e) => setMappingType(e.target.value)}>
                <option value="simple">Simple Path (single value)</option>
                <option value="filtered">Filtered Array (with condition)</option>
                <option value="all">All Array Items (concatenated)</option>
              </select>
            </div>

            {mappingType === 'simple' && (
              <div className="form-group">
                <label>API Response Path</label>
                <input
                  type="text"
                  value={simplePath}
                  onChange={(e) => setSimplePath(e.target.value)}
                  placeholder="e.g., title, release_date, overview"
                />
                {availablePaths.length > 0 && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <small style={{ color: '#666' }}>Available paths:</small>
                    <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #ddd', padding: '0.5rem', fontSize: '0.8rem' }}>
                      {availablePaths.filter(p => p.type !== 'array').map((path, index) => (
                        <div key={index} style={{ 
                          cursor: 'pointer', 
                          padding: '2px',
                          ':hover': { backgroundColor: '#f0f0f0' }
                        }}
                        onClick={() => setSimplePath(path.path)}>
                          <code>{path.path}</code> - {path.description}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {(mappingType === 'filtered' || mappingType === 'all') && (
              <>
                <div className="form-group">
                  <label>Array Path</label>
                  <select value={arrayPath} onChange={(e) => setArrayPath(e.target.value)}>
                    <option value="">Select array...</option>
                    {getArrayPaths().map((path, index) => (
                      <option key={index} value={path}>{path}</option>
                    ))}
                  </select>
                </div>

                {mappingType === 'filtered' && (
                  <>
                    <div className="form-group">
                      <label>Filter Field</label>
                      <select 
                        value={filterField} 
                        onChange={(e) => {
                          setFilterField(e.target.value);
                          setFilterValue(''); // Reset filter value when field changes
                        }}
                      >
                        <option value="">Select filter field...</option>
                        {arrayPath && getFieldsForArray(arrayPath).map((field, index) => (
                          <option key={index} value={field}>{field}</option>
                        ))}
                      </select>
                      {!arrayPath && (
                        <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem' }}>
                          Select an array path first
                        </p>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Filter Value</label>
                      <select
                        value={filterValue}
                        onChange={(e) => setFilterValue(e.target.value)}
                        disabled={!filterField}
                      >
                        <option value="">Select filter value...</option>
                        {filterField && getFilterValuesForField(arrayPath, filterField).map((value, index) => (
                          <option key={index} value={value}>{value}</option>
                        ))}
                      </select>
                      {!filterField && (
                        <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem' }}>
                          Select a filter field first
                        </p>
                      )}
                      {filterField && getFilterValuesForField(arrayPath, filterField).length === 0 && (
                        <p style={{ fontSize: '0.8rem', color: '#e74c3c', marginTop: '0.5rem' }}>
                          No values found for this field in the sample data
                        </p>
                      )}
                    </div>
                  </>
                )}

                <div className="form-group">
                  <label>Target Field (to extract)</label>
                  <select value={targetField} onChange={(e) => setTargetField(e.target.value)}>
                    <option value="">Select field...</option>
                    {arrayPath && getFieldsForArray(arrayPath).map((field, index) => (
                      <option key={index} value={field}>{field}</option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {/* Preview */}
            <div style={{ 
              marginTop: '1rem', 
              padding: '0.75rem', 
              backgroundColor: '#f8f9fa', 
              border: '1px solid #e9ecef',
              borderRadius: '4px'
            }}>
              <strong>Preview Expression:</strong>
              <br />
              <code>
                {mappingType === 'simple' && simplePath}
                {mappingType === 'filtered' && arrayPath && filterField && filterValue && targetField && 
                  `${arrayPath}[${filterField}=${filterValue}].${targetField}`}
                {mappingType === 'all' && arrayPath && targetField && 
                  `${arrayPath}[].${targetField}`}
                {(!simplePath && mappingType === 'simple') || 
                 (!arrayPath || !targetField) && (mappingType === 'filtered' || mappingType === 'all') ? 
                 'Incomplete expression' : ''}
              </code>
            </div>

            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: '1rem', 
              marginTop: '2rem' 
            }}>
              <button 
                type="button" 
                className="btn" 
                onClick={() => setShowBuilder(false)}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn" 
                onClick={saveMapping}
                disabled={
                  !editingField.trim() || 
                  (mappingType === 'simple' && !simplePath) ||
                  ((mappingType === 'filtered' || mappingType === 'all') && (!arrayPath || !targetField)) ||
                  (mappingType === 'filtered' && (!filterField || !filterValue))
                }
              >
                Save Mapping
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FieldMappingBuilder;